import { useTranslation } from 'react-i18next';
import { GridColumn } from 'src/components/GridWrapper';
import { DeleteButtonPermissions } from 'src/sections/view_invoice_budget_period/index';
import { getScrollbarWidth } from 'src/utils/getScrollBarWidth';
import { hasPermission } from 'src/utils/JWT';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../invoice_details/InvoiceDetailsContext';
import InvoiceAmountCell from './custom_cells/InvoiceAmountCell';
import InvoiceAmountFooterCell from './custom_cells/InvoiceAmountFooterCell';
import InvoiceBudgetPeriodCell from './custom_cells/InvoiceBudgetPeriodCell';
import InvoiceBudgetPeriodFooterCell from './custom_cells/InvoiceBudgetPeriodFooterCell';
import InvoiceDeleteActionCell from './custom_cells/InvoiceDeleteActionCell';
import InvoiceMpaDetailCell from './custom_cells/InvoiceMpaDetailCell';
import InvoiceMpaDetailHeaderCell from './custom_cells/InvoiceMpaDetailHeaderCell';
import InvoicePercentageCell from './custom_cells/InvoicePercentageCell';
import InvoicePercentageFooterCell from './custom_cells/InvoicePercentageFooterCell';
import InvoiceWarningCell from './custom_cells/InvoiceWarningCell';
import InvoiceWarningFooterCell from './custom_cells/InvoiceWarningFooterCell';

export const useInvoiceBudgetPeriodColumn = () => {
  const { t } = useTranslation();
  const mpaPermissionNumber = useContextSelector(
    InvoiceContext,
    (value) => value.mpaPermissionNumber
  );
  const isReadOnly = hasPermission(DeleteButtonPermissions, mpaPermissionNumber);

  const invoiceBudgetPeriodColumns = [
    {
      field: 'expanded',
      component: InvoiceMpaDetailCell,
      menu: false,
      headerCell: InvoiceMpaDetailHeaderCell,
      width: 45,
    },
    {
      field: 'errors',
      title: '',
      menu: false,
      component: InvoiceWarningCell,
      footerCell: InvoiceWarningFooterCell,
      width: 45,
    },
    {
      field: 'glPeriodId',
      title: t('Accounting Period'),
      dataType: 'string',
      component: InvoiceBudgetPeriodCell,
      footerCell: InvoiceBudgetPeriodFooterCell,
      menu: false,
      width: 300,
    },
    {
      field: 'percentageAmount',
      title: `% ${t('Distribution')}`,
      format: '{0:n2}',
      dataType: 'fixed',
      component: InvoicePercentageCell,
      footerCell: InvoicePercentageFooterCell,
      menu: false,
      width: 200,
    },
    {
      field: 'amount',
      title: `$ ${t('Distribution')}`,
      dataType: 'fixed',
      format: '{0:c2}',
      component: InvoiceAmountCell,
      footerCell: InvoiceAmountFooterCell,
      menu: false,
      width:150
    },
    ...(isReadOnly
      ? [
          {
            field: '',
            title: t('Actions'),
            component: InvoiceDeleteActionCell,
            menu: false,
            width: 146
          },
        ]
      : []),
  ] as GridColumn<any>[];
  return invoiceBudgetPeriodColumns;
};

export const useInvoiceBudgetPeriodSubColumn = (): GridColumn<any>[] => {
  const { t } = useTranslation();
  const scrollWidth = getScrollbarWidth();
  const invoiceBudgetPeriodSubColumns = [
    {
      field: 'glCode',
      title: t('GL Code'),
      dataType: 'string',
      menu: false,
      width: 300,
    },
    {
      field: 'glSubTotal',
      title: t('GL Subtotal'),
      dataType: 'dollar',
      format: '{0:c2}',
      menu: false,
    },
    {
      field: 'taxableAmount',
      title: t('Taxable Amt'),
      dataType: 'dollar',
      format: '{0:c2}',
      menu: false,
    },
    {
      field: 'discountAmt',
      title: t('Discount'),
      dataType: 'dollar',
      format: '{0:c2}',
      menu: false,
    },
    {
      field: 'shipCostAmt',
      title: t('Shipping'),
      dataType: 'dollar',
      format: '{0:c2}',
      menu: false,
    },
    {
      field: 'salesTaxAmt',
      title: t('Taxes'),
      dataType: 'dollar',
      format: '{0:c2}',
      menu: false,
    },
    {
      field: 'glTotal',
      title: t('GL Total'),
      dataType: 'dollar',
      format: '{0:c2}',
      menu: false,
      width: 100 - scrollWidth,
    },
  ] as GridColumn<any>[];

  return invoiceBudgetPeriodSubColumns;
};
